import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { message } from 'antd';
import { useSelected } from '../../../../../providers/selectedContext';
import { useTheme } from '../../../../../providers/theme';
import { getColors } from '../../../../utils/getColors';
import { calculateSample, updateSample } from '../../../../../api/data';
import {
  CalculateSamplePayload,
  RegressionIndexes,
  UpdateSamplePayload
} from '../../../../../@types/samples/types';

import {
  extractCurves,
  RawDataPoint,
  RegressionResult,
  ChartConfig
} from '../../logic';

import { generateRegressionData } from '../../logic';
import { calculateCoreyRegression } from '../../logic';
import { findOptimalSor } from '../../logic';
import { getChartOptions } from '../../logic';

export const useSonKroGasRegression = () => {
  const { isDay } = useTheme();
  const colors = getColors(isDay);
  const { selectedInfo, setSelectedInfo } = useSelected();
  const sample = selectedInfo?.sample;

  const [rawDataTable, setRawDataTable] = useState<RawDataPoint[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [sorg, setSorg] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [optimizing, setOptimizing] = useState<boolean>(false);

  const lastSampleIdForSelectionRef = useRef<string | undefined>(undefined);
  const lastSorgHydrationSampleRef = useRef<string | undefined>(undefined);
  const shouldHydrateSorgRef = useRef<boolean>(true);
  const resetPendingRef = useRef<boolean>(false);

  useEffect(() => {
    if (!sample) return;

    const isNewSampleContext = lastSorgHydrationSampleRef.current !== sample.uid;
    if (!isNewSampleContext && !shouldHydrateSorgRef.current) {
      return;
    }

    if (sample?.k_rel_values?.gas_oil?.true_sor_g != null) {
      setSorg(sample.k_rel_values.gas_oil.true_sor_g);
    } else if (sample?.k_rel_values?.gas_oil?.sor_g != null) {
      setSorg(sample.k_rel_values.gas_oil.sor_g);
    } else if (sample?.k_rel_values?.gas_oil?.sor != null) {
      setSorg(sample.k_rel_values.gas_oil.sor);
    } else {
      setSorg(0);
    }

    shouldHydrateSorgRef.current = false;
    lastSorgHydrationSampleRef.current = sample.uid;
  }, [sample]);

  useEffect(() => {
    setError(null);
    if (!sample) return;

    try {
      const { swArr, kArr, swi } = extractCurves(sample, 'gas_oil_kro');

      if (swi == null || sorg == null) {
        setRawDataTable([]);
        return;
      }

      const data = generateRegressionData(swArr, kArr, swi, sorg, 'decreasing');
      setRawDataTable(data);
    } catch (e: any) {
      setError(e.message);
      setRawDataTable([]);
    }
  }, [sample, sorg]);

  useEffect(() => {
    if (!sample || rawDataTable.length === 0) return;

    const currentDataKeys = new Set(rawDataTable.map(d => d.key));
    const isNewSample = lastSampleIdForSelectionRef.current !== sample.uid;

    lastSampleIdForSelectionRef.current = sample.uid;

    setSelectedRowKeys((prevSelected) => {
      let candidates: any[] = [];

      if (isNewSample) {
        candidates = sample.regression_indexes?.son_kro_g_selected_indexes || [];
        if (candidates.length === 0) return rawDataTable.map(d => d.key);
      } else {
        candidates = prevSelected;
        if (candidates.length === 0) candidates = sample.regression_indexes?.son_kro_g_selected_indexes || [];
      }

      const validKeys = candidates.filter((k: any) => currentDataKeys.has(Number(k)));

      if (validKeys.length > 0) return validKeys;

      return rawDataTable.map(d => d.key);
    });

  }, [sample, rawDataTable]);

  const regressionResult = useMemo<RegressionResult | null>(() => {
    if (rawDataTable.length < 2 || selectedRowKeys.length < 2) {
      return null;
    }
    try {
      const indices = selectedRowKeys.map(key => Number(key));
      return calculateCoreyRegression(rawDataTable, indices);
    } catch (e) {
      return null;
    }
  }, [rawDataTable, selectedRowKeys]);

  const handleSorgChange = useCallback((value: number) => {
    resetPendingRef.current = false;
    setSorg(value);
  }, []);

  const handleSelectedRowKeysChange = useCallback((keys: React.Key[]) => {
    resetPendingRef.current = false;
    setSelectedRowKeys(keys);
  }, []);

  const handleReset = useCallback(() => {
    const baselineSorg =
      sample?.k_rel_values?.gas_oil?.sor_g ??
      sample?.k_rel_values?.gas_oil?.sor ??
      0;
    setSorg(baselineSorg);
    setSelectedRowKeys(rawDataTable.map(point => point.key));
    resetPendingRef.current = true;
  }, [sample, rawDataTable]);

  const handleFindBestSorg = useCallback(() => {
    if (!sample || selectedRowKeys.length < 2) {
      message.warning('Please select at least 2 points to find optimal Sorg.');
      return;
    }
    setOptimizing(true);

    setTimeout(() => {
      try {
        const { swArr, kArr, swi } = extractCurves(sample, 'gas_oil_kro');
        const indices = selectedRowKeys.map(k => Number(k));

        const result = findOptimalSor(swArr, kArr, swi, indices);

        if (result) {
          setSorg(Number(result.bestSor.toFixed(4)));
          message.success(`Optimal Sorg found: ${result.bestSor.toFixed(4)} (R²: ${result.bestR2.toFixed(4)})`);
        } else {
          message.warning('Could not find a valid Sorg for these points.');
        }
      } catch (e: any) {
        message.error('Error optimizing Sorg: ' + e.message);
      } finally {
        setOptimizing(false);
      }
    }, 100);
  }, [sample, selectedRowKeys]);

  const handleSave = useCallback(async () => {
    if (!sample) return;
    if (!regressionResult) {
      message.error('Cannot save: Invalid regression result.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const indices = selectedRowKeys.map(key => Number(key));
      const existingIndexes = sample.regression_indexes || {};
      const indexesToSave: RegressionIndexes = {
        ...existingIndexes,
        son_kro_g_selected_indexes: indices,
      };

      const payload: UpdateSamplePayload = {
        sample_uid: sample.uid,
        regression_indexes: resetPendingRef.current
          ? {
              ...existingIndexes,
              son_kro_g_selected_indexes: [],
              sgn_krg_selected_indexes: [],
              refined_no_g_selected_indexes: [],
              refined_ng_selected_indexes: [],
            }
          : indexesToSave,
        true_sor_g: resetPendingRef.current ? null : sorg,
        true_kro_g: resetPendingRef.current ? null : regressionResult.a_max_calc,
      };

      const updatedSample = await updateSample(payload);
      const refreshPayload: CalculateSamplePayload = {
        sample_uid: updatedSample.uid,
        strategies_keys: ['gas-oil'],
        input_data_map: {
          'gas-oil': { type: 'GAS_OIL' }
        }
      };
      const recalculatedSample = await calculateSample(refreshPayload);
      shouldHydrateSorgRef.current = true;
      resetPendingRef.current = false;
      setSelectedInfo({ sample: recalculatedSample });
      message.success('Regression selection saved successfully!');
    } catch (e: any) {
      const saveError = e.message || 'Failed to save selection.';
      setError(saveError);
      message.warning(saveError);
    } finally {
      setLoading(false);
    }
  }, [sample, selectedRowKeys, regressionResult, sorg, setSelectedInfo]);

  const chartOptions = useMemo(() => {
    const chartConfig: ChartConfig = {
      title: 'Son vs Kro Regression',
      xAxisLabel: 'Son (Normalized)',
      yAxisLabel: 'Kro (Calculated)',
      tooltipLabelX: 'Son',
      tooltipLabelY: 'Kro',
      seriesNameRaw: 'All Lab Points (Normalized)',
      seriesNameSelected: 'Points in Regression',
      seriesNameFit: 'Kro Calc (Fit Line)',
      seriesColor: colors.gasOil,
      fitLineColor: 'red',
      xAxisReversed: true
    };

    return getChartOptions({
      regressionResult,
      rawDataTable,
      selectedRowKeys,
      config: chartConfig
    });
  }, [regressionResult, rawDataTable, selectedRowKeys, colors]);

  return {
    sample,
    sorg,
    setSorg: handleSorgChange,
    loading,
    optimizing,
    error,
    rawDataTable,
    selectedRowKeys,
    setSelectedRowKeys: handleSelectedRowKeysChange,
    regressionResult,
    chartOptions,
    handleFindBestSorg,
    handleReset,
    handleSave,
    colors,
    isDay
  };
};
